import { useMemo } from 'react'
import Styles from '../PropertyForm.module.scss'
import { FieldArray } from 'formik'
import { type PropertyValue } from 'api/clients/PropertyClient'
import { getRandomColor } from '../ColorInput'
import { ReactComponent as AddIcon } from 'assets/icons/add16.svg'
import { type Values as FormValues } from '../PropertyForm'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import SingleSelectItem from './SingleSelectItem'
import { Button } from 'components/Button'
import { SingleMultiSelectOptionsHeight } from '../TypeSelect'

interface Props {
  values: FormValues
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  isSubmitting: boolean
  propertyTypeChanged: boolean
  isValid: boolean
}

const MultiSelect = ({ values, setFieldValue, isSubmitting, propertyTypeChanged, isValid }: Props) => {
  const reorder = (propertyValues: PropertyValue[], startIndex: number, endIndex: number) => {
    const result = [...propertyValues]
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return
    }

    const items = reorder(values.property_values, result.source.index, result.destination.index)

    setFieldValue('property_values', items)
  }

  const optionsListContainerHeight = useMemo(
    () => values.property_values.length * SingleMultiSelectOptionsHeight,
    [values.property_values.length],
  )

  const renderDragAndDropOptionsList = (remove: (index: number) => void) => {
    if (values.property_values.length === 0) return null

    return (
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable
          droppableId="droppable"
          renderClone={(provided, _snapshot, rubric) => (
            <div
              key={rubric.source.index}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              ref={provided.innerRef}
              className={Styles.draggableOptionsItem}
            >
              <SingleSelectItem
                value={values.property_values[rubric.source.index]}
                index={rubric.source.index}
                onRemove={remove}
                isNew={!!!values.property_values[rubric.source.index].id}
                isFormValid={isValid}
                setFieldValue={setFieldValue}
              />
            </div>
          )}
        >
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={Styles.optionsListItems}
              style={{ height: `${optionsListContainerHeight}px` }}
            >
              {values.property_values.map((value: PropertyValue, index: number) => (
                <Draggable draggableId={index.toString()} index={index} key={`${index}-${value.id}`}>
                  {(provided, _snapshot) => (
                    <div
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      ref={provided.innerRef}
                      className={Styles.optionsItem}
                    >
                      <SingleSelectItem
                        value={value}
                        index={index}
                        onRemove={remove}
                        isNew={!!!values.property_values[index].id}
                        isFormValid={isValid}
                        setFieldValue={setFieldValue}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    )
  }

  return (
    <>
      <div className={Styles.sectionSubheader}>
        Multi select allows you to select multiple options from a list of values that you define.
      </div>
      {!propertyTypeChanged && (
        <>
          <FieldArray name="property_values">
            {({ remove, push }) => (
              <div className={Styles.optionsList}>
                {renderDragAndDropOptionsList(remove)}
                {isValid && (
                  <Button
                    size="lg"
                    variant="tertiaryGreen"
                    isDisabled={isSubmitting}
                    onPress={() =>
                      push({
                        value: `Option ${values.property_values.length + 1}`,
                        position: values.property_values.length,
                        meta: { color: getRandomColor() },
                      })
                    }
                    frontIcon={AddIcon}
                  >
                    Add an option
                  </Button>
                )}
              </div>
            )}
          </FieldArray>
        </>
      )}
    </>
  )
}

export default MultiSelect
